import { useMemo } from "react";
import Select, { MultiValue, StylesConfig } from "react-select";
import styled from "styled-components";
import { AgencyTagType } from "../types";
import { useReactSelectTheme } from "@bagel-web/components";
import {
  getAgencyTagFilter,
  getPrefixFiltersForAgencyTag,
  AgencyTagFilter,
} from "./AgencyTagFilter";

type AgencyTagOption = {
  value: string;
  label: string;
};

export type AgencyTagSelectProps = {
  value?: AgencyTagFilter;
  disabled?: boolean;
  onChange?: (e: { target: { value: AgencyTagFilter } }) => void;
  autoFocus?: boolean;
  agencyTags: Array<AgencyTagType>;
};

const SelectContainer = styled.div`
  width: 400px;
`;

const styleOverrides: StylesConfig<AgencyTagOption> = {
  container: (baseStyles) => ({
    ...baseStyles,
    minWidth: "300px",
  }),
  option: (baseStyles, { isSelected }) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: isSelected ? "white" : "var(--bs-code-color)",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: "var(--bs-code-color)",
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    fontFamily: "monospace",
    fontSize: ".875em",
    color: "var(--aa-tag-color)",
    backgroundColor: "var(--aa-tag-bg)",
    borderRadius: "6px",
    paddingLeft: "10px",
    paddingRight: "10px",
  }),
};

const AgencyTagSelect = ({
  agencyTags,
  disabled,
  onChange,
  autoFocus,
  value,
}: AgencyTagSelectProps) => {
  const options = useMemo(() => {
    const optionSet = new Set<string>();

    agencyTags.forEach((agencyTag) => {
      getPrefixFiltersForAgencyTag(agencyTag.tag_name).forEach(
        (filterOption) => {
          optionSet.add(filterOption);
        }
      );
    });

    return Array.from(optionSet.values()).map((option) => ({
      value: option,
      label: option,
    }));
  }, [agencyTags]);

  return (
    <SelectContainer>
      <Select
        isDisabled={disabled}
        isMulti={true}
        styles={styleOverrides}
        theme={useReactSelectTheme()}
        placeholder="Filter Agency Tags..."
        options={options}
        value={options.filter((option) => value?.tags.includes(option.value))}
        autoFocus={autoFocus}
        menuPortalTarget={document.body}
        onChange={(newValue: MultiValue<AgencyTagOption>) => {
          onChange?.({
            target: { value: getAgencyTagFilter(newValue.map((v) => v.value)) },
          });
        }}
      />
    </SelectContainer>
  );
};

export default AgencyTagSelect;
